import { useCallback, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm, useWatch } from "react-hook-form";
import baseUrl from "../../../api/baseUrl";
import socket from "../../../api/socket";
import CloseIcon from "@mui/icons-material/Close";
import FlagIcon from "@mui/icons-material/Flag";

interface AddBrandProps {
  closeModal: () => void;
  id?: string;
}

interface FormData {
  purposeName: string;
  color?: string;
}

/** Match AppointmentCounts card bg *-100 (light) palette — full opacity */
const DEFAULT_COLORS = [
  { name: "amber", value: "#fef3c7", picker: "#fef3c7" },
  { name: "orange", value: "#ffedd5", picker: "#ffedd5" },
  { name: "sky", value: "#e0f2fe", picker: "#e0f2fe" },
  { name: "lime", value: "#ecfccb", picker: "#ecfccb" },
  { name: "violet", value: "#ede9fe", picker: "#ede9fe" },
  {
    name: "green",
    value: "rgb(209 250 229 / var(--tw-bg-opacity, 1))",
    picker: "#d1fae5",
  },
  { name: "red", value: "#fee2e2", picker: "#fee2e2" },
  { name: "fuchsia", value: "#fae8ff", picker: "#fae8ff" },
] as const;

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function toPickerValue(color?: string): string {
  if (!color) return "#fef3c7";
  const preset = DEFAULT_COLORS.find(
    (c) =>
      c.name === color.toLowerCase() ||
      c.value.toLowerCase() === color.toLowerCase() ||
      c.picker.toLowerCase() === color.toLowerCase()
  );
  if (preset) return preset.picker;
  if (HEX_RE.test(color))
    return color.length === 4
      ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
      : color;
  return "#fef3c7";
}

const AddPurpose: React.FC<AddBrandProps> = ({ closeModal, id }) => {
  const methods = useForm<FormData>({
    defaultValues: { purposeName: "", color: "" },
  });
  const selectedColor = useWatch({ control: methods.control, name: "color" }) || "";

  const getBrandData = useCallback(
    (purposeid: string) => {
      baseUrl
        .get("client/setting/purpose/findPurposeById", {
          headers: { purposeid: purposeid },
        })
        .then((response) => {
          const data = response.data.data;
          methods.reset({
            purposeName: data?.purposeName ?? "",
            color: data?.color ?? "",
          });
        })
        .catch((error) => {
          console.error(error?.response);
        });
    },
    [methods]
  );

  useEffect(() => {
    if (id) {
      getBrandData(id);
    }
  }, [id, getBrandData]);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    (data) => {
      const payload = {
        purposeName: data.purposeName,
        color: data.color?.trim() || "",
      };
      const apiCall = id
        ? baseUrl.patch("client/setting/purpose/updatePurposeById", payload, {
            headers: { purposeid: id },
          })
        : baseUrl.post("client/setting/purpose/createPurpose", payload);

      apiCall
        .then((response) => {
          if (!id) socket.emit("addpurpose", response.data.data);
          closeModal();
        })
        .catch((error) => {
          console.error(error?.response);
        });
    },
    [closeModal, id]
  );

  const setColor = (value: string) => {
    methods.setValue("color", value, { shouldDirty: true });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FlagIcon className="text-white" sx={{ fontSize: 24 }} />
            <h2 className="text-lg font-bold text-white">
              {id ? "Update Purpose" : "Add Purpose"}
            </h2>
          </div>
          <button
            onClick={closeModal}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Form */}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Purpose Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...methods.register("purposeName", { required: true })}
                  type="text"
                  placeholder="Enter purpose name"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-slate-700">
                    Color <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  {selectedColor && (
                    <button
                      type="button"
                      onClick={() => setColor("")}
                      className="text-xs text-slate-500 hover:text-slate-700 underline"
                    >
                      Clear
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {DEFAULT_COLORS.map((c) => {
                    const active =
                      selectedColor.toLowerCase() === c.name ||
                      selectedColor.toLowerCase() === c.value.toLowerCase() ||
                      selectedColor.toLowerCase() === c.picker.toLowerCase();
                    return (
                      <button
                        key={c.name}
                        type="button"
                        title={c.name}
                        onClick={() => setColor(c.value)}
                        className={`w-9 h-9 rounded-full border-2 transition-all ${
                          active
                            ? "border-slate-800 scale-110 shadow-md"
                            : "border-white ring-1 ring-slate-200 hover:scale-105"
                        }`}
                        style={{ backgroundColor: c.value }}
                      />
                    );
                  })}
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={toPickerValue(selectedColor)}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-12 h-12 rounded-xl border-2 border-slate-200 cursor-pointer overflow-hidden bg-transparent"
                    title="Custom color"
                  />
                  <input
                    type="text"
                    value={selectedColor}
                    onChange={(e) => setColor(e.target.value)}
                    placeholder="e.g. #fef3c7"
                    className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all font-mono text-sm"
                  />
                  {selectedColor && (
                    <span
                      className="w-9 h-9 rounded-full border border-slate-200 shrink-0"
                      style={{ backgroundColor: selectedColor }}
                      title="Preview"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="px-5 py-2.5 text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:shadow-lg hover:shadow-amber-200 transition-all font-medium"
              >
                {id ? "Update" : "Submit"}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default AddPurpose;
